import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { CREATIVE_ACTIVITIES } from "@/data/creative-activities";

export const Route = createFileRoute("/creative-reflection")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Creative Reflection & Expression — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Gentle creative wellbeing activities for self-reflection, mindful creativity, and personal expression.",
      },
      {
        property: "og:title",
        content: "Creative Reflection & Expression — Recovery Trauma™",
      },
      {
        property: "og:description",
        content:
          "Mindful, creative wellbeing activities to support self-expression, reflection, and curiosity.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/creative-reflection" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/creative-reflection" }],
  }),
  component: withMemberGate(CreativeReflectionPage, "Creative Reflection"),
});

/**
 * Fire an anonymous conversion beacon when a user opens an activity from
 * this page. Reuses the existing pageview endpoint with a synthetic
 * `event:` path so the row appears in page_views alongside real navigations.
 */
function trackActivityOpen(slug: string) {
  if (typeof window === "undefined") return;
  try {
    const STORAGE_KEY = "rt_visitor_session";
    let sid = window.localStorage.getItem(STORAGE_KEY);
    if (!sid) {
      sid =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2) + Date.now().toString(36);
      window.localStorage.setItem(STORAGE_KEY, sid);
    }
    const body = JSON.stringify({
      session_id: sid,
      path: `event:creative_open:${slug}`,
      referrer: "/creative-reflection",
    });
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon("/api/public/pageview", blob);
    } else {
      void fetch("/api/public/pageview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* ignore */
  }
}

function CreativeReflectionPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:py-12">
      <header className="text-center">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-[var(--gold-deep)]">
          🎨 Creative Reflection &amp; Expression
        </p>
        <h1 className="mt-3 font-display text-3xl text-foreground sm:text-4xl md:text-5xl">
          Mindful creativity, gently expressed
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-base text-muted-foreground">
          Choose an activity and start drawing straight away. Each one opens a
          calm digital sketchbook with a gentle prompt to guide you.
        </p>
        <p className="mx-auto mt-3 max-w-xl text-xs text-muted-foreground/80">
          May help support calm, focus, grounding, and wellbeing. These are
          creative wellbeing activities, not therapy or clinical intervention.
        </p>
      </header>

      {/* Mobile: swipeable horizontal carousel */}
      <section className="mt-8 sm:hidden">
        <div className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain px-4 pb-4 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {CREATIVE_ACTIVITIES.map((a) => (
            <Link
              key={a.slug}
              to="/art-board"
              search={{ a: a.slug }}
              onClick={() => trackActivityOpen(a.slug)}
              className={`group block min-w-[78%] snap-center [scroll-snap-stop:always] rounded-3xl border border-gold/30 bg-gradient-to-br ${a.tone} via-card to-card p-5 text-start shadow-sm transition hover:border-gold/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-deep)] focus-visible:ring-offset-2 focus-visible:ring-offset-background`}
              aria-label={`${a.title} — open drawing canvas`}
            >
              <span aria-hidden="true" className="text-3xl">
                {a.emoji}
              </span>
              <h3 className="mt-2 font-display text-lg text-foreground">
                {a.title}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">{a.intro}</p>
              <span
                aria-hidden="true"
                className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[var(--gold-deep)]"
              >
                Open canvas <ArrowRight className="h-3 w-3" aria-hidden="true" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Desktop/tablet: compact card grid */}
      <section className="mt-10 hidden sm:block">
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {CREATIVE_ACTIVITIES.map((a) => (
            <li key={a.slug}>
              <Link
                to="/art-board"
                search={{ a: a.slug }}
                onClick={() => trackActivityOpen(a.slug)}
                className={`group flex h-full w-full flex-col gap-2 rounded-3xl border border-gold/25 bg-gradient-to-br ${a.tone} via-card to-card p-5 text-start transition hover:-translate-y-0.5 hover:border-gold/55 hover:shadow-[var(--shadow-gold)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-deep)] focus-visible:ring-offset-2 focus-visible:ring-offset-background`}
                aria-label={`${a.title} — open drawing canvas`}
              >
                <span aria-hidden="true" className="text-3xl">
                  {a.emoji}
                </span>
                <h3 className="font-display text-lg text-foreground">
                  {a.title}
                </h3>
                <p className="text-sm text-muted-foreground">{a.intro}</p>
                <span
                  aria-hidden="true"
                  className="mt-auto inline-flex items-center gap-1 pt-2 text-xs font-semibold text-[var(--gold-deep)]"
                >
                  Open canvas <ArrowRight className="h-3 w-3" aria-hidden="true" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Guided interactive experiences */}
      <section className="mt-8">
        <Link
          to="/creative-expression"
          className="group block rounded-3xl border border-gold/30 p-6 text-center shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-deep)] focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:p-8"
          style={{
            background:
              "linear-gradient(135deg, var(--qa-rose-tint) 0%, var(--qa-peach-tint) 25%, var(--qa-gold-tint) 50%, var(--qa-mint-tint) 75%, var(--qa-sky-tint) 100%)",
          }}
        >
          <span aria-hidden="true" className="ce-anim-float text-4xl">
            🌈
          </span>
          <h2 className="mt-2 font-display text-2xl text-foreground">
            Ten guided experiences to feel, draw &amp; let go
          </h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-foreground/75">
            Step-by-step journeys — an emotional iceberg, a worry jar, balloons
            to release, and more. Everything stays private to your account.
          </p>
          <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-card/85 px-5 py-2 text-xs font-bold text-foreground shadow-sm transition group-hover:gap-2">
            Explore the experiences <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </Link>
      </section>

      <section className="mt-12 rounded-3xl border border-gold/30 bg-cream/40 p-6 text-center md:p-8">
        <p className="mx-auto max-w-xl text-sm text-muted-foreground">
          Prefer a blank page? You can also open the sketchbook without a
          prompt and draw whatever comes.
        </p>
        <Link
          to="/art-board"
          className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 text-xs font-semibold text-[var(--gold-deep)] transition hover:bg-gold/10"
        >
          Open blank canvas <ArrowRight className="h-3 w-3" />
        </Link>
        <div className="mt-6">
          <Link
            to="/my-healing-journey"
            className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--gold-deep)]"
          >
            <ArrowLeft className="h-3 w-3" /> Back to My Recovery Journey
          </Link>
        </div>
      </section>
    </div>
  );
}
